import { Link } from "react-router-dom";
import { L, mediaUrl, useFetch, useTitle } from "../lib";
import { PageHeader, SampleTag, Reveal, Empty } from "../ui";

const SECTIONS = [["creativity", "Her creativity"], ["family", "Family life"], ["travels", "Travels"],
  ["traditions", "Traditions"], ["recipes", "Recipes"], ["expressions", "Favourite expressions"]];

function Timeline({ items, arts, stories }) {
  const artMap = Object.fromEntries((arts || []).map((a) => [a.id, a]));
  const storyMap = Object.fromEntries((stories || []).map((s) => [s.id, s]));
  return (
    <section className="max-w-5xl mx-auto px-6 lg:px-10 mt-28" data-testid="life-timeline">
      <h2 className="font-lou text-3xl sm:text-4xl font-light mb-12">Along the way</h2>
      <ol className="relative border-l border-lou-line ml-2">
        {items.map((t, i) => (
          <Reveal key={t.id} delay={i * 0.03}>
            <li className="pl-8 sm:pl-12 pb-14 relative" data-testid={`timeline-item-${t.id}`}>
              <span className="absolute -left-[5px] top-2 w-2.5 h-2.5 rounded-full bg-lou-ochre ring-4 ring-lou-ivory" aria-hidden />
              <p className="font-lou italic text-lg text-lou-clay min-h-[1.5rem]">{[t.date_text, t.place].filter(Boolean).join(" · ")}</p>
              <h3 className="font-lou text-2xl mt-2">{t.title} <SampleTag show={t.is_sample} className="align-middle ml-2" /></h3>
              {t.description && <p className="text-lou-soft mt-2 leading-relaxed max-w-2xl whitespace-pre-line">{t.description}</p>}
              {t.media_ids?.length > 0 && (
                <div className="flex gap-3 mt-4 flex-wrap">{t.media_ids.map((m) => <img key={m} src={mediaUrl(m)} alt="" className="h-28 w-auto" loading="lazy" />)}</div>
              )}
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm">
                {(t.artwork_ids || []).filter((a) => artMap[a]).map((a) => (
                  <Link key={a} to={L(`/art/${a}`)} className="text-lou-clay hover:underline">Artwork: {artMap[a].title || "Untitled"}</Link>
                ))}
                {(t.story_ids || []).filter((s) => storyMap[s]).map((s) => (
                  <Link key={s} to={L("/remembering")} className="text-lou-clay hover:underline">Memory: {storyMap[s].title || storyMap[s].display_name}</Link>
                ))}
              </div>
            </li>
          </Reveal>
        ))}
      </ol>
    </section>
  );
}

const List = ({ title, items, testid }) => items?.length ? (
  <div data-testid={testid}>
    <h3 className="text-xs tracking-[0.28em] uppercase text-lou-clay mb-4">{title}</h3>
    <ul className="space-y-2 text-lou-ink/85">{items.map((x) => <li key={x}>{x}</li>)}</ul>
  </div>
) : null;

function Career({ c }) {
  if (!c.awards?.length && !c.exhibitions?.length) return null;
  return (
    <section className="max-w-6xl mx-6 lg:mx-auto px-6 lg:px-12 py-14 mt-28 lou-wash-sand" data-testid="life-career">
      <h2 className="font-lou text-3xl sm:text-4xl font-light">Awards and exhibitions</h2>
      <div className="grid lg:grid-cols-12 gap-12 mt-10 border-t border-lou-line pt-12">
        <div className="lg:col-span-5" data-testid="life-awards">
          <h3 className="text-xs tracking-[0.28em] uppercase text-lou-clay mb-6">Awards</h3>
          <ol className="space-y-5">{(c.awards || []).map((w) => (
            <li key={w.year + w.text} className="grid grid-cols-[7.5rem_1fr] gap-4">
              <span className="font-lou italic text-xl text-lou-clay">{w.year}</span><span className="leading-snug">{w.text}</span></li>))}</ol>
          {c.awards_note && <p className="text-sm text-lou-soft mt-6" data-testid="life-awards-note">{c.awards_note}</p>}
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-10">
          <List title="Exhibitions" items={c.exhibitions} testid="life-exhibitions" />
          <List title="Watercolor and gouache exhibitions" items={c.painting_exhibitions} testid="life-painting-exhibitions" />
          <List title="Education" items={c.education} testid="life-education" />
          <List title="Affiliations" items={c.affiliations} testid="life-affiliations" />
          {c.collections && <p className="sm:col-span-2 text-sm text-lou-soft" data-testid="life-collections">Collections: {c.collections}</p>}
        </div>
      </div>
    </section>
  );
}

export default function Life() {
  useTitle("Her Life");
  const { data } = useFetch("/biography");
  const { data: arts } = useFetch("/artworks");
  const { data: stories } = useFetch("/stories");
  if (!data) return <div className="h-[70vh]" />;
  const b = data.biography || {};
  const secs = SECTIONS.filter(([k]) => b.sections?.[k]?.trim());
  const empty = !b.short_intro && !b.full_story && !secs.length && !data.timeline.length;
  return (
    <div data-testid="life-page">
      <PageHeader eyebrow="Her Life" title="The story of Louise" intro={b.short_intro} testid="life-intro" />
      {b.is_sample && <div className="max-w-6xl mx-auto px-6 lg:px-10 -mt-4"><SampleTag show /></div>}
      {empty && <div className="max-w-4xl mx-auto px-6"><Empty>The family is writing her story.</Empty></div>}
      {b.full_story && (
        <section className="max-w-3xl mx-auto px-6 lg:px-10 mt-10">
          <div className="lou-prose text-lg text-lou-ink/85 whitespace-pre-line first-letter:font-lou first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.9] first-letter:text-lou-clay" data-testid="life-full-story">{b.full_story}</div>
        </section>
      )}
      {secs.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 lg:px-10 mt-24 grid md:grid-cols-2 gap-x-16 gap-y-14">
          {secs.map(([k, t]) => (
            <Reveal key={k}>
              <div data-testid={`life-section-${k}`}>
                <h2 className="font-lou text-3xl font-light border-b border-lou-line pb-3">{t}</h2>
                <p className="mt-4 text-lou-soft leading-relaxed whitespace-pre-line">{b.sections[k]}</p>
              </div>
            </Reveal>
          ))}
        </section>
      )}
      {b.career && <Career c={b.career} />}
      {data.timeline.length > 0 && <Timeline items={data.timeline} arts={arts} stories={stories} />}
    </div>
  );
}

